import { planService, formatLimit, type PublicPlan } from '@/services/planService';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthContext';
import {
  TrendingUp,
  Zap,
  FlaskConical,
  Eye,
  Banknote,
  ArrowRight,
  LayoutDashboard,
  BarChart3,
  Globe,
  ShieldCheck,
  Share2,
  Workflow,
} from 'lucide-react';

const goLogin = () => {
  window.location.hash = '#/login';
};
const goRegister = () => {
  window.location.hash = '#/register';
};
const goDashboard = () => {
  window.location.hash = '#/dashboard';
};

const features: Array<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}> = [
  {
    icon: Zap,
    title: 'Live Scanner',
    body: 'Real-time scanning across equities, forex, and crypto — 30+ built-in strategies, custom filters, and instant alerts.',
  },
  {
    icon: FlaskConical,
    title: 'Matrix Backtester',
    body: 'Build your own strategies. Run hundreds of entry × management combinations side-by-side across years of tick data.',
  },
  {
    icon: Eye,
    title: 'Trade Viewer',
    body: 'Drill into every backtest trade — visualised against bar data, tick by tick. Share scenario links with anyone.',
  },
  {
    icon: Banknote,
    title: 'Paper & Live Trading',
    body: 'Wire automation rules to multiple brokers. Paper-trade to validate, then flip the kill switch when you\'re ready.',
  },
  {
    icon: Globe,
    title: 'Multi-Broker',
    body: 'Connect multiple brokers — equities and forex in one unified automation engine, with per-broker configuration.',
  },
  {
    icon: BarChart3,
    title: 'Performance Analytics',
    body: 'Equity curves, P&L histograms, MFE/MAE scatter plots, drawdown charts — every metric you need to trust a strategy.',
  },
  {
    icon: Workflow,
    title: 'Full Automation',
    body: 'Scanner hits → entry evaluation → position sizing → exit management → trade logging. Hands-free from signal to close.',
  },
  {
    icon: Share2,
    title: 'Scenario Sharing',
    body: 'Generate shareable links for any backtest scenario. Collaborators view the full trade breakdown — no account needed.',
  },
];

// Plans are fetched, never hardcoded. Names, prices and highlights live in the Plan documents
// so marketing can change them without a deploy — the whole reason the backend references
// plans by a stable id rather than by name.

export const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState<PublicPlan[]>([]);

  useEffect(() => {
    // Anonymous endpoint — a signed-out visitor is exactly who needs to read pricing. A
    // failure just leaves the section empty rather than breaking the landing page.
    planService.listPublic().then(setPlans).catch(() => setPlans([]));
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* ─── Top bar ─── */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-6xl mx-auto h-14 px-4 sm:px-6 flex items-center justify-between">
          <a href="#/" className="flex items-center gap-2 group">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-md shadow-primary/20">
              <TrendingUp className="h-4 w-4 text-white" />
            </div>
            <span className="text-base font-bold tracking-tight">
              <span>Trade</span>
              <span className="text-primary">Execute</span>
            </span>
          </a>
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <Button
                variant="default"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={goDashboard}
              >
                <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />
                Go to dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={goLogin}
                >
                  Sign in
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={goRegister}
                >
                  Get started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ─── Hero ─── */}
      {/* `isolate` creates a stacking context so the -z-10 background layers
          (radial glow + grid) stay scoped to this section and don't get
          painted behind the parent's solid bg-background. */}
      <section className="relative overflow-hidden isolate">
        {/* Ambient gradient background */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-60"
          style={{
            background:
              'radial-gradient(60% 60% at 50% 0%, hsl(var(--primary) / 0.18) 0%, transparent 70%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08]"
          style={{
            backgroundImage:
              'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            maskImage:
              'radial-gradient(ellipse at top, black 30%, transparent 75%)',
            WebkitMaskImage:
              'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border bg-card/50 px-3 py-1 text-[11px] text-muted-foreground mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Real-time market scanning + backtesting
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
            Backtest, paper-trade, then{' '}
            <span className="bg-gradient-to-br from-primary to-blue-500 bg-clip-text text-transparent">
              deploy with conviction.
            </span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            TradeExecute pairs a live multi-asset scanner — equities, forex, and
            crypto — with a matrix backtester for the strategies you bring, then
            wires the winners through multiple broker integrations into paper and
            live trading. One workflow from idea to live.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            {isAuthenticated ? (
              <Button
                variant="default"
                size="lg"
                className="w-full sm:w-auto"
                onClick={goDashboard}
              >
                Go to dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button
                  variant="default"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={goRegister}
                >
                  Get started
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={goLogin}
                >
                  Sign in
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ─── Features grid ─── */}
      <section className="border-t">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Everything you need to validate an edge
            </h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
              From live scanning to broker-integrated trading, in one tool.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-xl border bg-card/50 p-5 hover:bg-card transition-colors"
              >
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-sm font-semibold mb-1">{title}</div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  {body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Tier teaser ─── */}
      <section className="border-t bg-card/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Plans for every stage
            </h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
              Start free. Upgrade when your strategy starts paying for itself.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => {
              // The default plan is the one to highlight and the only one offering signup.
              const isEntry = plan.isDefault;
              return (
                <div
                  key={plan.id}
                  className={
                    'relative rounded-xl border p-5 flex flex-col ' +
                    (isEntry
                      ? 'border-primary/60 bg-primary/5 shadow-lg shadow-primary/10'
                      : 'bg-card/50')
                  }
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold">{plan.name}</div>
                    <span
                      className={
                        'text-[10px] px-2 py-0.5 rounded-full border ' +
                        (isEntry
                          ? 'border-primary/40 bg-primary/10 text-primary'
                          : 'border-border bg-muted text-muted-foreground')
                      }
                    >
                      {plan.monthlyPriceUsd > 0 ? `$${plan.monthlyPriceUsd}/mo` : 'Free'}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground leading-relaxed flex-1">
                    {plan.description}
                  </div>
                  <ul className="mt-3 space-y-1 text-[11px] text-muted-foreground">
                    <li>{formatLimit(plan.highlights.customScanners)} custom scanners</li>
                    <li>{formatLimit(plan.highlights.strategies)} strategies</li>
                    <li>{formatLimit(plan.highlights.backtestMinutesPerMonth)} backtest min/mo</li>
                    {plan.highlights.liveTrading && <li>Live trading</li>}
                  </ul>
                  {isEntry && !isAuthenticated && (
                    <Button
                      variant="default"
                      size="sm"
                      className="mt-4 h-8 text-xs"
                      onClick={goRegister}
                    >
                      Create free account
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="border-t">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Ready to put a strategy through its paces?
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {isAuthenticated
              ? 'Jump back into the dashboard and pick up where you left off.'
              : 'Sign up in under a minute. No credit card.'}
          </p>
          <div className="mt-6 flex flex-col items-center gap-3">
            {isAuthenticated ? (
              <Button variant="default" size="lg" onClick={goDashboard}>
                Go to dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button variant="default" size="lg" onClick={goRegister}>
                  Get started
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <div className="text-xs text-muted-foreground">
                  Already have an account?{' '}
                  <a
                    href="#/login"
                    className="text-primary hover:underline font-medium"
                  >
                    Sign in
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded-md bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
              <TrendingUp className="h-3 w-3 text-white" />
            </div>
            <span>
              &copy; {new Date().getFullYear()} TradeExecute. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="#/legal/terms" className="hover:text-foreground transition-colors">
              Terms
            </a>
            <a href="#/legal/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </a>
            <a href="#/legal/risk" className="hover:text-foreground transition-colors">
              Risk Disclaimer
            </a>
            <a href="mailto:support@tradeexecute.com" className="hover:text-foreground transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
